/**
 * Tools service for company dashboard
 * Manages AI agent tools / API integrations
 */

const API_BASE_URL = 'https://universal-ai-agent-backend.diypa571.workers.dev'

interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
  message?: string
}

export interface ToolParameter {
  id?: number
  parameter_name?: string
  name: string
  parameter_type?: string
  type: string
  description: string
  is_required?: boolean
  isRequired: boolean
  default_value?: string
  defaultValue?: string
  enum_values?: string
  enumValues?: string[]
  validation_regex?: string
  validationRegex?: string
}

export interface Tool {
  id: number
  tool_id: string
  company_id: number
  name: string
  display_name: string
  displayName?: string
  description: string
  api_url: string
  apiUrl?: string
  api_method: string
  apiMethod?: string
  api_headers?: Record<string, string>
  apiHeaders?: Record<string, string>
  api_body_template?: string
  apiBodyTemplate?: string
  response_path?: string
  responsePath?: string
  error_path?: string
  errorPath?: string
  timeout_ms: number
  timeoutMs?: number
  max_retries: number
  maxRetries?: number
  is_active: boolean
  created_at: string
  updated_at: string
  assigned_agent_ids?: number[]
  parameters?: ToolParameter[]
  usage_count_7d?: number
}

class ToolsService {
  private getToken(): string | null {
    return localStorage.getItem('token')
  }

  private getHeaders(): HeadersInit {
    const token = this.getToken()
    return {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    }
  }

  /**
   * Get all tools for company
   */
  async getTools(agentId?: number): Promise<ApiResponse<Tool[]>> {
    try {
      let url = `${API_BASE_URL}/api/tools`
      if (agentId) {
        url += `?agentId=${agentId}`
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: this.getHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch tools' }))
        return { success: false, error: error.message }
      }

      const data = await response.json()
      return { success: true, data: data.data }
    } catch (error) {
      console.error('Get tools error:', error)
      return { success: false, error: 'Failed to fetch tools' }
    }
  }

  /**
   * Create a new tool
   */
  async createTool(tool: {
    name: string
    displayName: string
    description: string
    apiUrl: string
    apiMethod: string
    apiHeaders?: Record<string, string>
    apiBodyTemplate?: string
    responsePath?: string
    errorPath?: string
    timeoutMs?: number
    maxRetries?: number
    parameters: ToolParameter[]
    agentIds: number[]
  }): Promise<ApiResponse<{ toolId: string }>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tools`, {
        method: 'POST',
        headers: this.getHeaders(),
        body: JSON.stringify(tool)
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to create tool' }))
        return { success: false, error: error.message }
      }

      const data = await response.json()
      return { success: true, data: data.data }
    } catch (error) {
      console.error('Create tool error:', error)
      return { success: false, error: 'Failed to create tool' }
    }
  }

  /**
   * Update a tool
   */
  async updateTool(toolId: string, updates: {
    displayName?: string
    description?: string
    apiUrl?: string
    apiMethod?: string
    apiHeaders?: Record<string, string>
    apiBodyTemplate?: string
    responsePath?: string
    errorPath?: string
    timeoutMs?: number
    maxRetries?: number
    isActive?: boolean
    parameters?: ToolParameter[]
    agentIds?: number[]
  }): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tools/${toolId}`, {
        method: 'PUT',
        headers: this.getHeaders(),
        body: JSON.stringify(updates)
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to update tool' }))
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      console.error('Update tool error:', error)
      return { success: false, error: 'Failed to update tool' }
    }
  }

  /**
   * Delete a tool
   */
  async deleteTool(toolId: string): Promise<ApiResponse<void>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tools/${toolId}`, {
        method: 'DELETE',
        headers: this.getHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to delete tool' }))
        return { success: false, error: error.message }
      }

      return { success: true }
    } catch (error) {
      console.error('Delete tool error:', error)
      return { success: false, error: 'Failed to delete tool' }
    }
  }

  /**
   * Get tool execution logs
   */
  async getToolLogs(toolId: string, limit: number = 50): Promise<ApiResponse<any[]>> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/tools/${toolId}/logs?limit=${limit}`, {
        method: 'GET',
        headers: this.getHeaders()
      })

      if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to fetch logs' }))
        return { success: false, error: error.message }
      }

      const data = await response.json()
      return { success: true, data: data.data }
    } catch (error) {
      console.error('Get tool logs error:', error)
      return { success: false, error: 'Failed to fetch logs' }
    }
  }
}

export const toolsService = new ToolsService()

